import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react'
import PropTypes from 'prop-types'
import { PublicClientApplication } from '@azure/msal-browser'

import { env } from 'admin/lib/config/env-config.js'
import { config, authenticationParameters } from 'admin/msal-config'

import { AuthContext } from './contexts'

let msalInstance = null
let msalPromise = null

function getMsal() {
  if (msalInstance) return Promise.resolve(msalInstance)

  if (!msalPromise) {
    msalPromise = PublicClientApplication.createPublicClientApplication(
      config
    ).then(instance => {
      msalInstance = instance
      return instance
    })
  }

  return msalPromise
}

export const msal = new Proxy(
  {},
  {
    get(target, prop) {
      if (!msalInstance) {
        throw new Error('MSAL not initialized. Call getMsal() first.')
      }
      return msalInstance[prop]
    }
  }
)

let redirectPromise = null

const debugAuth = false
function debug(...args) {
  if (debugAuth && env.NODE_ENV === 'development') {
    console.debug('[getToken]', ...args)
  }
}

export function AuthWrapper(props) {
  const getToken = useCallback(async () => {
    const msalInstance = await getMsal()

    if (!redirectPromise) {
      redirectPromise = msalInstance.handleRedirectPromise()
    }

    const result = await redirectPromise

    debug('[getToken] Invoked')
    const account = result?.account ?? msalInstance.getAllAccounts()[0]
    try {
      if (!account) throw Error('NO_ACCOUNT')

      debug('[getToken] Account Found')
      const auth = await msalInstance.acquireTokenSilent({
        ...authenticationParameters,
        forceRefresh: false,
        account
      })

      debug('[getToken] OK')
      return auth.accessToken
    } catch (e) {
      debug('[getToken] Error Caught', e)

      await msalInstance.acquireTokenRedirect({
        ...authenticationParameters,
        account
      })

      await msalInstance.handleRedirectPromise()

      return null
    }
  }, [])

  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false

    async function run() {
      debug('[getToken] Fetching Initial Token')
      await getToken()

      if (!cancelled) {
        setReady(true)
      }
    }

    // MSAL is just... the worst
    // Stop a race condition by only acquirring tokens after MSAL has flushed its processing behind the scenes
    setTimeout(() => {
      run()
    }, 1000)

    return () => {
      cancelled = true
    }
  }, [getToken])

  const auth = useMemo(() => {
    return {
      ready,
      getToken
    }
  }, [getToken, ready])

  return <AuthContext.Provider {...props} value={auth} />
}

AuthWrapper.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * @returns {{
    ready: boolean,
    getToken: () => Promise<string>
    }} auth
 */
export function useAuth() {
  return useContext(AuthContext)
}
